import Ajax from './Ajax'
import { action, makeAutoObservable, reaction, runInAction } from 'mobx'
import listSets from './misc/listSets.json'
import { IAdvisorAccount, IField, IPage, IQuestionnaire, ITemplateBody, IUpload } from './interfaces'
import { dateExpressionRegex, evaluateDateExpressionString } from './misc/dateValidation'
import { getJwtClaim } from './misc/utils'

export default class AppContext {

    advisorAccount: IAdvisorAccount;
    questionnaire: IQuestionnaire;

    answers: any;
    preloadedAnswers: any;
    unsubmittedValues: any;
    uploads: IUpload[];
    template: ITemplateBody;

    submitting: boolean;
    listSets: any;
    pageIndex: number;
    errors: string[];
    showPreviousAnswers: boolean;
    showSettings: boolean;
    redirectCountdown: number;
    embedded: boolean;
    token: string;

    globals = {
        "$undefined": undefined,
    }

    constructor() {

        this.answers = {}
        this.preloadedAnswers = {}
        this.uploads = []
        this.listSets = listSets
        this.errors = []

        this.embedded = window.self !== window.top
        this.token = window.localStorage.getItem("aora_questionnaire_jwt")

        makeAutoObservable(this)

        reaction(() => this.token, token => {
            if (token) {
                window.localStorage.setItem("aora_questionnaire_jwt", token)
            } else {
                window.localStorage.removeItem("aora_questionnaire_jwt")
                this.reset()
            }
        })

        // Scroll to the top on each page change.
        reaction(() => this.pageIndex, pageIndex => {
            if (this.embedded) {
                window.parent.postMessage({scrollTop: true}, "*")
            }
        })

        // If emebedded, continuously notify parent window of page height so it can resize.
        if (this.embedded) {
            
            document.getElementsByTagName('html')[0].style.overflowY = 'hidden';
            var lastScrollHeight = document.body.scrollHeight
            
            setInterval(() => {
                if (lastScrollHeight !== document.body.scrollHeight) {
                    lastScrollHeight = document.body.scrollHeight
                    window.parent.postMessage({scrollHeight: document.body.scrollHeight}, "*")
                }
            }, 200)
        }
    }

    get respondentEmail (): string {
        return this.questionnaire?.respondent?.respondentEmail
    }

    get locked (): boolean {
        return this.questionnaire?.locked
    }

    get completionRedirectUri (): string {
        if (this.questionnaire?.completionRedirectUri) {
            return this.questionnaire?.completionRedirectUri
        }
        
        var completionRedirectUriFromJwt = getJwtClaim("completion_redirect_override")
        if (completionRedirectUriFromJwt) {
            return completionRedirectUriFromJwt
        }

        return null
    }


    //#region Application utils
    //------------------------------------------------------------------

    login = async (questionnaireUid, accessCode) => {
        var response = await Ajax.Public.Login(questionnaireUid, accessCode)
        this.token = response.data.token
        this.loadQuestionnaire(questionnaireUid)
    }

    logout = async () => {
        if (this.completionRedirectUri) {
            this.setRedirectCountdown(this.completionRedirectUri, 5)
        }

        this.setToken(null)
    }

    saveAndQuit = async () => {
        if (this.unsubmittedValues) {
            await this.submitPageAnswers(this.unsubmittedValues, false)
        }
        this.logout()
    }

    setToken = (token) => {
        this.token = token
    }

    reset = () => {
        var defaultInstance = new AppContext()
        // Preserve advisorAccount so logo can remain in header after logout
        // Preserve redirect properties
        var propertiesToReset = ["questionnaire", "answers", "preloadedAnswers", "uploads", "template", "submitting", "listSets", "pageIndex", "errors", "showPreviousAnswers", "showSettings", "embedded", "token"]
        
        for (const key in defaultInstance) {
            if (propertiesToReset.includes(key)) {
                this[key] = defaultInstance[key]
            }
        }
    }

    setRedirectCountdown = (url: string, seconds: number) => {
        
        this.redirectCountdown = seconds

        var redirectInterval = setTimeout(() => {
            
            console.log(this.redirectCountdown, url)
            
            if (this.redirectCountdown > 0) {
                
                this.setRedirectCountdown(url, this.redirectCountdown - 1)
            
            } else if (this.redirectCountdown === 0 && url) {

                window.location.replace(url)

            } else {

                clearInterval(redirectInterval)
            
            }
        }, 1000)
    }

    displayError = (errorMessage) => {
        runInAction(() => {
            this.errors.push(errorMessage)
        })
    }

    dismissError = (errorMessage) => {
        runInAction(() => {
            this.errors = this.errors.filter((e) => e !== errorMessage)
        })
    }

    getQuestionnaireTitle = () => {
        if (!this.answers || !this.template) {
            return null
        }
        if ("subject_first_name" in this.answers) {
            return `${this.template.name}: ${this.answers["subject_first_name"]} ${this.answers["subject_family_name"] ? this.answers["subject_family_name"] : ""}`
        }
        if ("job_title" in this.answers) {
            return `${this.template.name}: ${this.answers["job_title"]}`
        }
        return this.template.name
    }

    //#endregion


    //#region Questionnaire utils
    //------------------------------------------------------------------
    evaluateConditions = (fieldOrPage: IField | IPage, answers: any) => {

        if (!Array.isArray(fieldOrPage.conditions)) {
            return true
        }

        var conditionsSatisfied = fieldOrPage.conditions.filter(conditionDict => {
            return this.evaluateCondition(conditionDict, answers)
        })

        var result = conditionsSatisfied.length > 0
        // console.log("Conditions result", result)
        return fieldOrPage.conditionsHide ? !result : result
    }

    evaluateCondition = (condition: any, answers: any) => {
        var conditionsSatisfied = Object.keys(condition).filter((fieldName, o) => {
        
            var valueToLookFor = condition[fieldName]

            if (typeof valueToLookFor === "string" && valueToLookFor.match(dateExpressionRegex)) {
                var result = evaluateDateExpressionString(answers, valueToLookFor)
                return !!result
            }

            var answer = answers[fieldName]
            var valuesToLookFor = Array.isArray(valueToLookFor) ? valueToLookFor : [valueToLookFor]
            // This allows us to make reference to values in the answers and define conditions like
            // {
            //      "subject_applying_from": ["$subject_country_of_birth", "$undefined"]
            // }
            valuesToLookFor = valuesToLookFor.map(value => value.startsWith("$") ? answers[value] : value)
            
            return valuesToLookFor.includes(answer)
        })

        var result = conditionsSatisfied.length === Object.keys(condition).length
        // console.log(" - Condition result", result)
        return result
    }

    isHidden = (fieldOrPage, values = {}) => {

        var answers = {...this.globals, ...this.answers, ...values}

        if (fieldOrPage.conditions) {
            return !this.evaluateConditions(fieldOrPage, answers)
        }

        var conditionDict = {...fieldOrPage.dependsOn, ...fieldOrPage.dependsOnAny, ...fieldOrPage.hideDependsOn, ...fieldOrPage.hideDependsOnAny}

        var conditionsSatisfied = Object.keys(conditionDict).filter((fieldName) => {
            
            var valueToLookFor = conditionDict[fieldName]

            if (typeof valueToLookFor === "string" && valueToLookFor.match(dateExpressionRegex)) {
                var result = evaluateDateExpressionString(answers, valueToLookFor)
                return !!result
            }

            var answer = answers[fieldName]
            var valuesToLookFor = Array.isArray(valueToLookFor) ? valueToLookFor : [valueToLookFor]
            // This allows us to make reference to values in the answers and define conditions like
            // {
            //      "subject_applying_from": ["$subject_country_of_birth", "$undefined"]
            // }
            valuesToLookFor = valuesToLookFor.map(value => value.startsWith("$") ? answers[value] : value)
            
            return valuesToLookFor.includes(answer)
        })

        if (fieldOrPage.dependsOn) {
            return Object.keys(conditionDict).length !== conditionsSatisfied.length
        }
        if (fieldOrPage.hideDependsOn) {
            return !(Object.keys(conditionDict).length !== conditionsSatisfied.length)
        }

        if (fieldOrPage.dependsOnAny) {
            return conditionsSatisfied?.length === 0
        }
        if (fieldOrPage.hideDependsOnAny) {
            return !(conditionsSatisfied?.length === 0)
        }

        return false
    }

    private getPageList = (answers, returnSubmittedPages): IPage[] => {

        var pageList = []

        const handlePages = (pages: IPage[]) => {
            pages.forEach(page => {

                if (!this.isHidden(page)) {
                    pageList.push(page)
                }

                // Check for conditional pages
                page.fields?.forEach(field => {
                    var value = answers[field.name]
                    if (field.conditional_pages) {
                        if (value in field.conditional_pages) {
                            handlePages(field.conditional_pages[value])
                        } else if ("else" in field.conditional_pages) {
                            handlePages(field.conditional_pages["else"])
                        }
                    }
                })
            })
        }

        handlePages(this.template.pages)

        pageList = pageList.map((page, i) => {
            return {...page, index: i}
        })

        if (!answers.submitted_pages) {
            return returnSubmittedPages ? [] : pageList
        }

        if (returnSubmittedPages) {
            return pageList.filter(page => answers.submitted_pages.includes(page.id))
        }

        return pageList.filter(page => !answers.submitted_pages.includes(page.id))
    }

    // Remove any answers which belong to fields which have been conditionally hidden
    cleanAnwers = (answers) => {

        var visibleFieldNames = []
        var pages = [...this.getPageList(answers, true)]
        
        if (this.template.settings && answers["settings_confirmed"] === "true") {
            pages.push(this.template.settings)
        }

        // Get list of field keys for fields which are visible
        pages.forEach((page) => {
            if (page?.table?.list_name) {
                visibleFieldNames.push(page.table?.list_name)
            }
            page?.fields?.forEach(field => {
                if (!this.isHidden(field, answers)) {
                    visibleFieldNames.push(field.name)
                }
            })
        })

        // Delete any value which doesn't have a visible field
        var metaKeys = ["submitted_pages", "percentage_complete"]
        Object.keys(answers).forEach(key => {
            if (!visibleFieldNames.includes(key) && !metaKeys.includes(key)) {
                delete answers[key]
            }
        })

        return answers
    }

    // If page is hidden, set submitted to false so that if it later becomes unhidden, it will be the next page shown
    resetSubmittedPages = (answers) => {

        var visiblePages = this.getPageList(answers, true)
        var remainingPages = this.getPageList(answers, false)

        var submittedPages = answers.submitted_pages.filter((pageId: string) => {
            return !!visiblePages.find((page) => page.id === pageId)
        })

        // Remove duplicates
        answers.submitted_pages = [...new Set(submittedPages)]

        // Calculate percentage_complete
        var totalPagesLength = visiblePages.length + remainingPages.length
        console.log(visiblePages.length, totalPagesLength)
        var percentageComplete = Math.ceil((visiblePages.length / totalPagesLength) * 100)
        answers.percentage_complete = percentageComplete.toString()

        return answers
    }

    updateAnswers = (answersToAdd, pageComplete = true) => {

        var page = this.getPage()

        var newAnswers = {
            ...this.answers,
            ...answersToAdd,
            submitted_pages: [...this.answers.submitted_pages, page.id]
        }
        
        newAnswers = this.resetSubmittedPages(newAnswers)
        newAnswers = this.cleanAnwers(newAnswers)

        // Remove page id after cleanAnswers so that answers on current page aren't cleared
        if (!pageComplete) {
            newAnswers.submitted_pages = newAnswers.submitted_pages.filter(pageId => pageId !== page.id)
        }

        return newAnswers
    }

    uploadFile = async (field: IField, file: File, evidenceType: string): Promise<IUpload> => {
        
        var formData = new FormData()
        formData.append("file", file)

        var response = evidenceType === 'passport'
            ? await Ajax.Upload.CreateAndExtract(this.questionnaire.uid, file.name, evidenceType, formData)
            : await Ajax.Upload.Create(this.questionnaire.uid, file.name, evidenceType, formData)

        var uploadResponse = await Ajax.Upload.Get(response.data)
        var upload = uploadResponse.data

        this.setUploads([...this.uploads, upload])

        return upload
    }

    // If pageId is not set, answers will be saved but the page won't be tracked as submitted
    submitPageAnswers = action(async (answers, pageComplete = true) => {

        Object.keys(answers).forEach(fieldKey => {
            var answer = answers[fieldKey]
            if (typeof answer === "string" && answer.length) {
                answers[fieldKey] = answer.trim()
            }
        })

        this.submitting = true
        this.errors = []
        var newAnswers = this.updateAnswers(answers, pageComplete)

        await Ajax.Questionnaire.Update(this.questionnaire.uid, newAnswers).then(action((response) => {
            this.pageIndex = null
            if (pageComplete) {
                window.scrollTo(0, 0)
            }
            if (this.embedded) {
                window.parent.postMessage({scrollTop: true}, "*")
            }
        })).catch(() => {
            this.displayError("Failed to save")
        })

        this.answers = newAnswers
        this.submitting = false
    })

    submitQuestionnaire = action(async () => {
        this.submitting = true

        await Ajax.Questionnaire.Submit(this.questionnaire.uid).then(action(async () => {
            var redirectUri = this.completionRedirectUri
            if (redirectUri) {
                this.setRedirectCountdown(redirectUri, 5)
            }

            this.questionnaire = {...this.questionnaire, locked: true}
            this.answers = {}
        })).catch(() => {
            this.displayError("Failed to submit questionnaire")
        })

        this.submitting = false
    })

    loadQuestionnaire = async (id = this.questionnaire.uid) => {

        await Ajax.Questionnaire.Get(id).then(action((response) => {

            document.title = response.data.template.displayName
            this.questionnaire = response.data
            this.errors = []

            if (this.questionnaire.locked) {
                this.answers = null
                this.preloadedAnswers = null
                this.uploads = null
            } else {
                this.uploads = response.data.uploads

                try {
                    this.answers = JSON.parse(response.data.answers)

                    // answers.submitted_pages MUST be defined
                    if (!this.answers.submitted_pages || !Array.isArray(this.answers.submitted_pages)) {
                        this.answers.submitted_pages = []
                    }

                } catch (e) {
                    console.log("Error parsing answers", e)
                    this.answers = {}
                }

                try {
                    this.preloadedAnswers = JSON.parse(response.data.preloadedAnswers)
                } catch (e) {
                    console.log("Error parsing preloadedAnswers", e)
                    this.preloadedAnswers = {}
                }

                try {
                    this.template = JSON.parse(response.data.template.json)
                } catch (e) {
                    console.log("Error parsing template", e)
                    this.template = null
                }
            }
        })).catch(() => {
            // this.displayError("Failed to load questionnaire")
        })
    }

    formatDataString = (upload): string => {
        
        if (!upload?.data) {
            return null
        }
        
        var uploadType = upload.fileFormat.toLowerCase()
        var fileFormats = this.getListSet("file_formats")
        
        if (!Object.keys(fileFormats).includes(uploadType)) {
            return null
        }
        
        var mimeType = fileFormats[uploadType]
        return `data:${mimeType};base64,${upload.data}`

    }

    formatLabel = (message) => {
        if (message && typeof message === "string") {
            var placeholders = message.match(/{[a-zA-Z\-_0-9]*}/g)
            if (placeholders && Array.isArray(placeholders)) {
                for (var i = 0; i < placeholders.length; i++) {
                    var placeholder = placeholders[i]
                    var attribute_key = placeholder.replace("{", "").replace("}", "")
                    var value = this.answers[attribute_key]
                    if (value) {
                        message = message.replace(`${placeholder}`, value.replace("_", " "))
                    }
                }
            }
        }
        
        return message
    }

    backPage = () => {
        runInAction(() => {
            var page = this.getPage()
            if (!page) {
                var submittedPages = this.getSubmittedPages()
                if (submittedPages.length > 0) {
                    page = submittedPages.reverse()[0]   
                }
            }
            if (page) {
                this.pageIndex = page.index - 1
            }
        })
    }

    getAdvisorAccount = async (advisorUid: string) => {
        await Ajax.Public.GetAdvisorAccount(advisorUid).then(action((response) => {
            this.advisorAccount = response.data as IAdvisorAccount
        }))
    }

    getListSet = (setKey, sort = true) => {
        if (Object.keys(this.listSets).includes(setKey)) {

            var sortedListSet = {}
            var listSet = this.listSets[setKey]

            var items = Object.keys(listSet).map(function(key) {
                return [key, listSet[key]];
            })

            if (sort) {
                // Sort the array based on the second element
                items.sort((a, b) => a[1].localeCompare(b[1]))
            }

            items.forEach((item) => {
                sortedListSet[item[0]] = item[1]
            })

            return sortedListSet
        }

        return {}
    }

    getPage = () => {

        if ((this.template.settings && this.showSettings) || (this.template.settings && this.answers["settings_confirmed"] !== "true")) {
            return this.template.settings
        }

        if (this.pageIndex != null) {
            var submittedPages = this.getSubmittedPages()
            return submittedPages[this.pageIndex]
        }
        
        var unsubmittedPages = this.getUnsubmittedPages()
        if (unsubmittedPages.length) {
            return unsubmittedPages[0]
        }

        return null
    }

    getSubmittedPages = () => {
        return this.getPageList(this.answers, true)
    }

    getUnsubmittedPages = () => {
        return this.getPageList(this.answers, false)
    }

    togglePreviousAnswers = (show: boolean = null) => {
        this.showPreviousAnswers = typeof show === "boolean" ? show : !this.showPreviousAnswers
    }

    toggleSettings = (show: boolean = null) => {
        this.showSettings = typeof show === "boolean" ? show : !this.showSettings
    }

    setUploads = action((uploads: IUpload[]) => {
        this.uploads = uploads
    })

    setSubmitting = (submitting: boolean) => {
        runInAction(() => {
            this.submitting = submitting
        })
    }

    setPageIndex = (index: number) => {
        runInAction(() => {
            this.pageIndex = index
        })
    }

    //#endregion
}